import { useMemo, useState, type PropsWithChildren } from 'react';
import { map } from 'rxjs/operators';
import { SecuritiesContext } from '../../contexts/SecuritiesContext';
import { useCachedSubscription } from '../../hooks/useCachedSubscription';
import { useConstant } from '../../hooks/useConstant';
import { wsSubscriptionMemory } from '../../pipes/wsSubscriptionMemory';
import { wsWaitForAllPages } from '../../pipes/wsWaitForAllPages';
import { SECURITY } from '../../tokens/requestTypes';
import type { Security } from '../../types/Security';
import { ProductTypeEnum } from '../../types/types';
import { getSearchSymbol } from '../../utils';
import { useCFDIndexes } from './cfdIndexes';
import { useFutureIndexes } from './futureIndexes';
import { useOptionIndexes } from './optionIndexes';
import { useOTCIndexes } from './otcIndexes';
import { usePerpIndexes } from './perpIndexes';
import { usePredictionIndexes } from './predictionIndexes';
import { useCoreSecurityIndexes } from './SecuritiesProviderIndexes';
import { useSpotIndexes } from './spotIndexes';
import { isValidSecurity } from './utils';

export {
  getPredictionOutcomeEventID,
  UNLINKED_PREDICTION_EVENT_ID,
  type PredictionSecurityIndexes,
} from './predictionIndexes';
export {
  getCurrencyPairFromSecurity,
  getExpirationByMarketByCurrencyIdentity,
  getFuturesByMarketByCurrencyPair,
  getStrikesByOptionKey,
  isActiveSecurity,
} from './SecuritiesProviderIndexes';

const PRODUCT_TYPES_WITH_BLOTTER_DESCRIPTION = new Set<Security['ProductType']>([
  ProductTypeEnum.Share,
  ProductTypeEnum.GovernmentBond,
  ProductTypeEnum.Future,
]);

function prepareSecurity(security: Security) {
  security.searchSymbol = getSearchSymbol(security.Symbol);
  security.showBlotterDescription = PRODUCT_TYPES_WITH_BLOTTER_DESCRIPTION.has(security.ProductType) ? true : undefined;
}

export const SecuritiesProvider = function SecuritiesProvider({
  children,
  paginate = false,
}: PropsWithChildren<{ paginate?: boolean }>) {
  // make sure to never change the security request... for example of paginate flips, user has to refresh the page to re-make the changed request
  const securityRequest = useConstant({ name: SECURITY, tag: 'SecuritiesProvider', paginate } as const);
  const { data: subscription } = useCachedSubscription<Security>(securityRequest);
  const [loadingRecordsCount, setLoadingRecordsCount] = useState<number>();

  // The provider owns one shared memory stream. Each grouped index hook derives
  // its own observable from the latest subscription message.
  const securityMemoryObs = useMemo(
    () =>
      subscription.pipe(
        map(message => {
          const securities = (message.data ?? []).filter(isValidSecurity);
          for (const security of securities) {
            prepareSecurity(security);
          }
          return { ...message, data: securities };
        }),
        wsWaitForAllPages({
          getUniqueKey: s => s.Symbol,
          onPageReceived: ({ loadedCount, isComplete }) => {
            setLoadingRecordsCount(isComplete ? undefined : loadedCount);
          },
        }),
        wsSubscriptionMemory(security => security.Symbol)
      ),
    [subscription]
  );

  const coreIndexes = useCoreSecurityIndexes(securityMemoryObs);
  const perpIndexes = usePerpIndexes(securityMemoryObs);
  const cfdIndexes = useCFDIndexes(securityMemoryObs);
  const futureIndexes = useFutureIndexes(securityMemoryObs);
  const optionIndexes = useOptionIndexes(securityMemoryObs);
  const spotIndexes = useSpotIndexes(securityMemoryObs);
  const otcIndexes = useOTCIndexes(securityMemoryObs);
  const predictionIndexes = usePredictionIndexes(securityMemoryObs);

  const contextValue = useMemo(
    () => ({
      ...coreIndexes,
      loadingRecordsCount,
      securityMemoryObs,
      searchableSecurities: coreIndexes.searchableSecurities!,
      ...perpIndexes,
      ...cfdIndexes,
      ...futureIndexes,
      ...optionIndexes,
      ...spotIndexes,
      ...otcIndexes,
      ...predictionIndexes,
    }),
    [
      cfdIndexes,
      coreIndexes,
      futureIndexes,
      loadingRecordsCount,
      optionIndexes,
      otcIndexes,
      perpIndexes,
      predictionIndexes,
      securityMemoryObs,
      spotIndexes,
    ]
  );

  return <SecuritiesContext.Provider value={contextValue}>{children}</SecuritiesContext.Provider>;
};
